/**
 * Lūneli case builder: Step 01 (shell) + Step 02 (backs) + cart rail + zoom.
 *
 * Two different data paths, deliberately:
 *
 *   Step 01 (shells) reads /collections/{handle}/products.json. There are only
 *   a handful of shell products, so the payload is small and client-side
 *   variant matching is fine.
 *
 *   Step 02 (backs) is server-rendered. It asks Shopify's Section Rendering API
 *   for sections/luneli-backs-grid.liquid against the backs collection with a
 *   storefront variant filter applied, so Shopify returns only the designs that
 *   fit the chosen phone — roughly 50-100 KB per model instead of the several
 *   megabytes the old products.json approach downloaded.
 *
 * The filter's URL parameter is discovered at runtime from the section's
 * data-filter-param, never hardcoded, so renaming the filter in Search &
 * Discovery cannot silently break the page.
 */
(function () {
  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function initBuilder(root) {
    if (!root || root.dataset.luneliReady === 'true') return;
    root.dataset.luneliReady = 'true';

    var scope = root.parentNode || document;
    var Products = window.LuneliProducts;
    var Models = window.LuneliModels;

    var shellCollections = {};
    try {
      shellCollections = JSON.parse(root.dataset.shellCollections || '{}');
    } catch (e) {
      shellCollections = window.LUNELI_SHELL_COLLECTION || {};
    }
    var FREE_SHIP = Number(root.dataset.freeShip || 40) * 100; // cents

    var els = {
      shellColorBtns: Array.prototype.slice.call(root.querySelectorAll('[data-shell-color]')),
      shellImage: root.querySelector('[data-shell-image]'),
      shellNote: root.querySelector('[data-shell-note]'),
      shellLineName: root.querySelector('[data-shell-line-name]'),
      shellPriceNote: root.querySelector('[data-shell-price-note]'),
      addShellBtn: root.querySelector('[data-add-shell]'),
      addBundleBtn: root.querySelector('[data-add-bundle]'),
      bundlePrice: root.querySelector('[data-bundle-price]'),
      cards: root.querySelector('[data-cards]'),
      backsCount: root.querySelector('[data-backs-count]'),
      backsStatus: root.querySelector('[data-backs-status]'),
      collectionPills: root.querySelector('[data-collection-pills]'),
      searchInput: root.querySelector('[data-backs-search]'),
      searchClear: root.querySelector('[data-backs-search-clear]'),
      searchNote: root.querySelector('[data-search-note]'),
      sortSelect: root.querySelector('[data-backs-sort]'),
      showMoreBtn: root.querySelector('[data-show-more]'),
      showLessBtn: root.querySelector('[data-show-less]'),
      cartRail: root.querySelector('[data-cart-rail]'),
      cartHeader: root.querySelector('[data-cart-header]'),
      cartToggle: root.querySelector('[data-cart-toggle]'),
      cartToggleLabel: root.querySelector('[data-cart-toggle-label]'),
      cartChevron: root.querySelector('[data-cart-chevron]'),
      cartSummary: root.querySelector('[data-cart-summary]'),
      shipMsg: root.querySelector('[data-ship-msg]'),
      shipFill: root.querySelector('[data-ship-fill]'),
      cartLines: root.querySelector('[data-cart-lines]'),
      cartEmpty: root.querySelector('[data-cart-empty]'),
      subtotal: root.querySelector('[data-subtotal]'),
    };

    var zoom = { overlay: scope.querySelector('#luneli-zoom') || document.getElementById('luneli-zoom') };
    if (zoom.overlay) {
      zoom.modal = zoom.overlay.querySelector('[data-zoom-modal]');
      zoom.close = zoom.overlay.querySelector('[data-zoom-close]');
      zoom.name = zoom.overlay.querySelector('[data-zoom-name]');
      zoom.sub = zoom.overlay.querySelector('[data-zoom-sub]');
      zoom.image = zoom.overlay.querySelector('[data-zoom-image]');
      zoom.nav = zoom.overlay.querySelector('[data-zoom-nav]');
      zoom.prev = zoom.overlay.querySelector('[data-zoom-prev]');
      zoom.next = zoom.overlay.querySelector('[data-zoom-next]');
      zoom.dots = zoom.overlay.querySelector('[data-zoom-dots]');
      zoom.price = zoom.overlay.querySelector('[data-zoom-price]');
      zoom.add = zoom.overlay.querySelector('[data-zoom-add]');
      zoom.desc = zoom.overlay.querySelector('[data-zoom-desc]');
      zoom.descToggle = zoom.overlay.querySelector('[data-zoom-desc-toggle]');
      zoom.rating = zoom.overlay.querySelector('[data-zoom-rating]');
      zoom.reviews = zoom.overlay.querySelector('[data-zoom-reviews]');
      zoom.allReviews = zoom.overlay.querySelector('[data-zoom-all-reviews]');
      zoom.moreReviews = zoom.overlay.querySelector('[data-zoom-more-reviews]');
      zoom.tabs = Array.prototype.slice.call(zoom.overlay.querySelectorAll('[data-zoom-tab]'));
      zoom.panels = {
        details: zoom.overlay.querySelector('[data-zoom-panel="details"]'),
        reviews: zoom.overlay.querySelector('[data-zoom-panel="reviews"]'),
      };
    }

    var state = {
      brand: 'iPhone',
      model: '',
      shellColor: 'Black',
      backsCollection: root.dataset.backsCollection || 'all-backs',
      collection: root.dataset.backsCollection || 'all-backs',
      backsSection: root.dataset.backsSection || 'luneli-backs-grid',
      detailSection: root.dataset.detailSection || 'luneli-product-detail',
      filterParam: root.dataset.filterParam || 'filter.v.option.phone-model',
      sort: 'manual',
      query: '',
      page: 1,
      hasNext: false,
      requestToken: 0,
    };

    var shellDataCache = {};
    var detailCache = {};
    var detailToken = 0;
    var zoomState = null;
    var cartItemCount = 0;
    var lastCart = null;
    var cartInFlight = null;
    var shellPreview = null;
    var bundlePreview = null;
    var loadingAll = null;

    // Typing fewer than this many characters is almost always a slip, and
    // filtering on one letter just makes the grid flicker.
    var SEARCH_MIN = 2;
    // A hard ceiling on how much we will pull in to satisfy one search. Eight
    // pages is ~400 designs; past that the shopper wants a category, not a grid.
    var MAX_SEARCH_PAGES = 8;

    function money(cents) { return window.LuneliCart.money(cents); }

    function fetchJSON(url) {
      return fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) {
        if (!r.ok) throw new Error('Request failed (' + r.status + '): ' + url);
        return r.json();
      });
    }

    function collectionProducts(handle, limit) {
      return fetchJSON(
        window.LuneliCart.root() + 'collections/' + handle + '/products.json?limit=' + (limit || 250)
      ).then(function (data) {
        return (data.products || []).map(Products.normalize).filter(Boolean);
      });
    }

    // ---------- Step 01: Shell ----------

    function loadShellData(brand) {
      if (shellDataCache[brand]) return Promise.resolve(shellDataCache[brand]);
      var handle = shellCollections[brand] || (window.LUNELI_SHELL_COLLECTION || {})[brand];
      if (!handle) {
        shellDataCache[brand] = { basic: null, bundle: null, error: 'No shell collection configured for ' + brand + '.' };
        return Promise.resolve(shellDataCache[brand]);
      }
      return collectionProducts(handle, 50)
        .then(function (products) {
          var shells = products.filter(function (p) { return p.product_type === 'Case Shell'; });
          var bundle = shells.find(function (p) { return /bundle/i.test(p.title); }) || null;
          var basic = shells.find(function (p) { return p !== bundle; }) || null;
          var result = { basic: basic, bundle: bundle, error: shells.length ? null : 'No shells published in "' + handle + '".' };
          shellDataCache[brand] = result;
          return result;
        })
        .catch(function (err) {
          console.error('[Lūneli builder] shell collection failed', err);
          var result = { basic: null, bundle: null, error: 'Could not load shells right now.' };
          shellDataCache[brand] = result;
          return result;
        });
    }

    /**
     * Exact model + colour match only. There is deliberately no "close enough"
     * fallback: falling back to another model's variant silently sells the
     * shopper a shell that does not fit their phone.
     */
    function findShellVariant(product, model, color) {
      if (!product) return null;
      return product.variants.find(function (v) {
        return Models.matches(Products.variantModel(v), model) && Products.variantColor(v) === color;
      }) || null;
    }

    function findBundleVariant(product, model) {
      if (!product) return null;
      return product.variants.find(function (v) {
        return Models.matches(Products.variantModel(v), model);
      }) || null;
    }

    function resetShellStep(message) {
      shellPreview = null;
      bundlePreview = null;
      if (els.shellImage) els.shellImage.removeAttribute('src');
      if (els.shellNote) els.shellNote.textContent = message || '';
      if (els.shellLineName) els.shellLineName.textContent = 'Select your model above';
      if (els.shellPriceNote) els.shellPriceNote.textContent = '';
      if (els.addShellBtn) {
        els.addShellBtn.disabled = true;
        els.addShellBtn.textContent = 'Choose a model first';
        delete els.addShellBtn.dataset.variantId;
      }
      if (els.addBundleBtn) {
        els.addBundleBtn.disabled = true;
        els.addBundleBtn.textContent = 'Choose a model first';
        delete els.addBundleBtn.dataset.variantId;
      }
      if (els.bundlePrice) els.bundlePrice.textContent = '—';
    }

    function renderShellStep() {
      var shellData = shellDataCache[state.brand];
      var basic = shellData && shellData.basic;
      var bundle = shellData && shellData.bundle;

      els.shellColorBtns.forEach(function (btn) {
        btn.classList.toggle('is-active', btn.dataset.shellColor === state.shellColor);
      });

      if (shellData && shellData.error) { resetShellStep(shellData.error); return; }
      if (!state.model || !basic) {
        resetShellStep(state.model ? '' : 'Select your model above to see the matching shell.');
        return;
      }

      var variant = findShellVariant(basic, state.model, state.shellColor);
      if (variant) {
        var img = variant.image || basic.images[0];
        if (img) els.shellImage.src = img;
        els.shellImage.alt = state.model + ' shell in ' + state.shellColor;
        els.shellNote.textContent = state.model + ', ' + state.shellColor + '.';
        els.shellLineName.textContent = state.model + ' shell, ' + state.shellColor;
        els.shellPriceNote.textContent = money(variant.price) + ' one-time · the part that protects, stays on';
        els.addShellBtn.disabled = !variant.available;
        els.addShellBtn.textContent = variant.available ? 'Add shell, ' + money(variant.price) : 'Sold out in this color';
        els.addShellBtn.dataset.variantId = variant.id;
        shellPreview = {
          title: basic.title,
          variantTitle: state.model + ' / ' + state.shellColor,
          price: variant.price,
          image: img || '',
        };
      } else {
        els.shellImage.removeAttribute('src');
        els.shellNote.textContent = 'No ' + state.shellColor + ' shell listed for the ' + state.model + ' yet.';
        els.shellLineName.textContent = state.model + ' shell, ' + state.shellColor;
        els.shellPriceNote.textContent = '';
        els.addShellBtn.disabled = true;
        els.addShellBtn.textContent = 'Not available for this model';
        delete els.addShellBtn.dataset.variantId;
        shellPreview = null;
      }

      var bundleVariant = findBundleVariant(bundle, state.model);
      if (bundleVariant) {
        var saving = bundleVariant.compare_at_price && bundleVariant.compare_at_price > bundleVariant.price
          ? ' (save ' + money(bundleVariant.compare_at_price - bundleVariant.price) + ')'
          : '';
        els.bundlePrice.textContent = money(bundleVariant.price) + saving;
        els.addBundleBtn.disabled = !bundleVariant.available;
        els.addBundleBtn.textContent = bundleVariant.available ? 'Add both' : 'Sold out';
        els.addBundleBtn.dataset.variantId = bundleVariant.id;
        bundlePreview = {
          title: bundle.title,
          variantTitle: state.model,
          price: bundleVariant.price,
          image: bundleVariant.image || (bundle.images && bundle.images[0]) || '',
        };
      } else {
        els.bundlePrice.textContent = '—';
        els.addBundleBtn.disabled = true;
        els.addBundleBtn.textContent = 'Not available for this model';
        delete els.addBundleBtn.dataset.variantId;
        bundlePreview = null;
      }
    }

    els.shellColorBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.shellColor = btn.dataset.shellColor;
        renderShellStep();
      });
    });

    if (els.addShellBtn) {
      els.addShellBtn.addEventListener('click', function () {
        var id = els.addShellBtn.dataset.variantId;
        if (id) addToCartFlow(els.addShellBtn, id, 1, shellPreview);
      });
    }
    if (els.addBundleBtn) {
      els.addBundleBtn.addEventListener('click', function () {
        var id = els.addBundleBtn.dataset.variantId;
        if (id) addToCartFlow(els.addBundleBtn, id, 1, bundlePreview);
      });
    }

    /**
     * Paint the rail from what the page already knows, before the network is
     * involved. Adding used to wait on two sequential round trips — POST
     * /cart/add, then GET /cart.js — so the collection sat visibly stale for
     * most of a second after every click. The requests still happen; they now
     * reconcile a rail that is already showing the right thing.
     */
    function optimisticAdd(variantId, quantity, preview) {
      if (!lastCart || !preview) return;
      var cart = {
        item_count: lastCart.item_count + quantity,
        total_price: lastCart.total_price + preview.price * quantity,
        items: lastCart.items.map(function (item) {
          return Object.assign({}, item);
        }),
      };
      var line = cart.items.find(function (item) {
        return String(item.variant_id) === String(variantId);
      });
      if (line) {
        line.quantity += quantity;
        line.line_price += preview.price * quantity;
      } else {
        cart.items.push({
          variant_id: variantId,
          product_title: preview.title || '',
          variant_title: preview.variantTitle || '',
          image: preview.image || '',
          line_price: preview.price * quantity,
          quantity: quantity,
        });
      }
      renderCart(cart);
    }

    function addToCartFlow(button, variantId, quantity, preview) {
      var original = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding…';
      optimisticAdd(variantId, quantity, preview);
      window.LuneliCart.add(variantId, quantity)
        .then(function () {
          button.textContent = '✓ Added';
          refreshCart();
          setTimeout(function () {
            button.textContent = original;
            button.disabled = false;
          }, 1400);
        })
        .catch(function (err) {
          console.error('[Lūneli builder] add to cart failed', err);
          button.textContent = 'Couldn’t add — retry';
          button.disabled = false;
          refreshCart(); // put the optimistic line back the way the server has it
        });
    }

    // ---------- Step 02: Backs, server-rendered ----------

    function backsUrl(model, page) {
      var url = window.LuneliCart.root() + 'collections/' + state.collection;
      var params = ['section_id=' + encodeURIComponent(state.backsSection)];
      if (model) params.push(state.filterParam + '=' + encodeURIComponent(model));
      // Shopify does the ordering server-side, so "Best selling" reflects real
      // sales across the whole filtered set rather than the page on screen.
      if (state.sort) params.push('sort_by=' + encodeURIComponent(state.sort));
      if (page && page > 1) params.push('page=' + page);
      return url + '?' + params.join('&');
    }

    function fetchBacks(model, page) {
      return fetch(backsUrl(model, page), { headers: { Accept: 'text/html' } })
        .then(function (r) {
          if (!r.ok) throw new Error('Backs request failed (' + r.status + ')');
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var payload = doc.querySelector('[data-backs-payload]');
          if (!payload) throw new Error('Backs section returned no payload');
          return payload;
        });
    }

    /** Everything the cart rail needs to draw a line before the server answers. */
    function cardPreview(card, variant) {
      if (!card || !variant) return null;
      return {
        title: card.dataset.productTitle || '',
        variantTitle: [state.model, variant.color].filter(Boolean).join(' / '),
        price: variant.price,
        image: variant.image || '',
      };
    }

    function activeVariant(card) {
      var variants = parseVariants(card);
      return (
        variants.find(function (v) { return String(v.id) === String(card.dataset.activeVariant); }) ||
        variants[0] ||
        null
      );
    }

    function parseVariants(card) {
      try {
        return JSON.parse(card.dataset.variants || '[]');
      } catch (e) {
        console.error('[Lūneli builder] bad variant payload on card', e);
        return [];
      }
    }

    function swatchHex(name) {
      var n = String(name || '').toLowerCase();
      var map = [
        ['tan', '#DCC6A8'], ['brown', '#7A5237'], ['black', '#26242E'], ['blue green', '#7FB6B0'], ['green', '#8CBF9A'],
        ['blue', '#5C7FBF'], ['pink', '#EC90B4'], ['peach', '#F3C3A2'], ['plum', '#7C3A52'], ['purple', '#8C6BB1'],
        ['red', '#D1344B'], ['gold', '#C9975A'], ['yellow', '#EBC351'], ['mustard', '#C9A227'], ['sunset', '#E9825C'],
        ['gray', '#7C7C86'], ['white', '#FFFFFF'], ['clear', '#EAF0F2'], ['usa', '#B5573F'], ['bright', '#F0C46A'],
        ['dark', '#3C3A47'],
      ];
      for (var i = 0; i < map.length; i++) if (n.indexOf(map[i][0]) !== -1) return map[i][1];
      return '#C9BFB4';
    }

    function selectCardVariant(card, variantId) {
      var variants = parseVariants(card);
      var v = variants.find(function (x) { return String(x.id) === String(variantId); });
      if (!v) return;
      card.dataset.activeVariant = String(v.id);

      var img = card.querySelector('.lu-back-card__img');
      if (img && v.image) {
        // Liquid's image_tag emits a srcset, and srcset wins over src — so the
        // src swap alone left the old artwork on screen. Drop both responsive
        // attributes before setting the new source.
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = v.image;
      }

      var price = card.querySelector('[data-card-price]');
      if (price) price.textContent = money(v.price);

      var add = card.querySelector('[data-card-add]');
      if (add) {
        add.dataset.variantId = v.id;
        add.disabled = !v.available;
        add.textContent = v.available ? '+ Add' : 'Sold out';
      }

      var dots = card.querySelectorAll('.lu-swatch-dot');
      Array.prototype.slice.call(dots).forEach(function (dot) {
        dot.classList.toggle('is-active', dot.dataset.variantId === String(v.id));
      });
    }

    function hydrateCards(container) {
      Array.prototype.slice.call(container.querySelectorAll('[data-back-card]')).forEach(function (card) {
        if (card.dataset.luHydrated === 'true') return;
        card.dataset.luHydrated = 'true';

        var variants = parseVariants(card);
        if (variants.length) card.dataset.activeVariant = String(variants[0].id);

        var swatches = card.querySelector('[data-swatches]');
        if (!swatches || variants.length < 2) return;
        variants.forEach(function (v, i) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'lu-swatch-dot' + (i === 0 ? ' is-active' : '');
          dot.dataset.variantId = String(v.id);
          dot.title = v.color || '';
          dot.setAttribute('aria-label', v.color || 'Colour option');
          dot.style.background = swatchHex(v.color);
          swatches.appendChild(dot);
        });
      });
    }

    function setBacksStatus(message) {
      if (!els.backsStatus) return;
      els.backsStatus.hidden = !message;
      els.backsStatus.textContent = message || '';
    }

    function applyBacksPayload(payload, append) {
      // The section reports the real filter parameter; trust it over our default.
      var reported = payload.getAttribute('data-filter-param');
      if (reported) state.filterParam = reported;

      var filterFound = payload.getAttribute('data-filter-found') === 'true';
      state.hasNext = payload.getAttribute('data-has-next') === 'true';
      state.page = parseInt(payload.getAttribute('data-current-page'), 10) || 1;
      var total = parseInt(payload.getAttribute('data-total'), 10) || 0;

      if (!filterFound) {
        els.cards.innerHTML = '';
        setBacksStatus(
          'The phone model filter isn’t switched on in Search & Discovery yet, so designs can’t be filtered.'
        );
        if (els.backsCount) els.backsCount.textContent = '';
        if (els.showMoreBtn) els.showMoreBtn.hidden = true;
        return;
      }

      var grid = payload.querySelector('.lu-cards-grid');
      if (!grid) {
        if (!append) els.cards.innerHTML = '';
        var note = payload.querySelector('.lu-backs-note');
        setBacksStatus(note ? note.textContent.trim() : 'No designs found.');
        if (els.backsCount) els.backsCount.textContent = '';
        if (els.showMoreBtn) els.showMoreBtn.hidden = true;
        return;
      }

      setBacksStatus('');
      if (append) {
        while (grid.firstChild) els.cards.appendChild(grid.firstChild);
      } else {
        els.cards.innerHTML = '';
        while (grid.firstChild) els.cards.appendChild(grid.firstChild);
      }
      hydrateCards(els.cards);

      var shownNow = els.cards.querySelectorAll('[data-back-card]').length;
      if (els.backsCount) {
        els.backsCount.textContent = state.model
          ? 'Showing ' + shownNow + ' of ' + total + ' designs for ' + state.model
          : '';
      }
      if (els.showMoreBtn) {
        els.showMoreBtn.hidden = !state.hasNext;
        if (state.hasNext) {
          els.showMoreBtn.textContent = 'Show more designs (' + Math.max(0, total - shownNow) + ' left)';
        }
      }
      if (els.showLessBtn) els.showLessBtn.hidden = state.page <= 1;
      if (state.query) applySearchFilter();
    }

    // ---------- Search and sort ----------

    function normText(value) {
      return String(value == null ? '' : value).toLowerCase().replace(/\s+/g, ' ').trim();
    }

    function setSearchNote(message) {
      if (!els.searchNote) return;
      els.searchNote.hidden = !message;
      els.searchNote.textContent = message || '';
    }

    /**
     * Search hides cards rather than re-querying, because the whole set for
     * this phone is already in the DOM by the time it runs (see runSearch).
     * That keeps typing instant instead of firing a request per keystroke.
     */
    function applySearchFilter() {
      if (!els.cards) return;
      var q = normText(state.query);
      var cards = Array.prototype.slice.call(els.cards.querySelectorAll('[data-back-card]'));
      var shown = 0;
      cards.forEach(function (card) {
        var hit = !q || normText(card.dataset.productTitle).indexOf(q) !== -1;
        card.hidden = !hit;
        if (hit) shown++;
      });

      if (!q) {
        setBacksStatus('');
        if (els.backsCount) {
          els.backsCount.textContent = state.model && cards.length
            ? 'Showing ' + cards.length + ' design' + (cards.length === 1 ? '' : 's') + ' for ' + state.model
            : '';
        }
        if (els.showMoreBtn) els.showMoreBtn.hidden = !state.hasNext;
        return;
      }

      // While a search is running the pager is meaningless — everything that
      // could match is already loaded.
      if (els.showMoreBtn) els.showMoreBtn.hidden = true;
      if (els.backsCount) {
        els.backsCount.textContent = shown + ' of ' + cards.length + ' designs match';
      }
      setBacksStatus(shown === 0 ? 'No designs match “' + state.query + '” for the ' + state.model + '.' : '');
    }

    /**
     * Pull in the remaining pages for the current phone so a search covers
     * every design that fits it, not just the page on screen. Capped, and only
     * ever triggered by someone actually typing.
     */
    function loadAllPages() {
      if (loadingAll) return loadingAll;
      var fetched = 0;
      function step() {
        if (!state.hasNext || fetched >= MAX_SEARCH_PAGES) return Promise.resolve();
        fetched++;
        return loadBacks(state.model, state.page + 1, true).then(function () {
          applySearchFilter();
          return step();
        });
      }
      loadingAll = step().then(function () { loadingAll = null; }, function (err) {
        loadingAll = null;
        throw err;
      });
      return loadingAll;
    }

    function runSearch() {
      if (!els.searchInput) return;
      var raw = els.searchInput.value.trim();
      if (els.searchClear) els.searchClear.hidden = !raw;
      state.query = raw.length >= SEARCH_MIN ? raw : '';

      if (!state.query) {
        setSearchNote('');
        applySearchFilter();
        return;
      }
      if (!state.model) {
        setSearchNote('Pick a phone model above, then search the designs that fit it.');
        return;
      }

      applySearchFilter();
      if (state.hasNext) {
        setSearchNote('Checking every design that fits your ' + state.model + '…');
        loadAllPages()
          .then(function () {
            applySearchFilter();
            setSearchNote(state.hasNext ? 'Showing the first few hundred designs. Narrow it down with a collection above.' : '');
          })
          .catch(function (err) {
            console.error('[Lūneli builder] search preload failed', err);
            setSearchNote('Couldn’t load every design — showing the ones already here.');
          });
      } else {
        setSearchNote('');
      }
    }

    function clearSearch() {
      if (els.searchInput) els.searchInput.value = '';
      if (els.searchClear) els.searchClear.hidden = true;
      state.query = '';
      setSearchNote('');
    }

    if (els.searchInput) {
      var searchTimer = null;
      els.searchInput.addEventListener('input', function () {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(runSearch, 200);
      });
      // Fires when the native "×" inside a type=search field is used.
      els.searchInput.addEventListener('search', runSearch);
      els.searchInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); clearTimeout(searchTimer); runSearch(); }
      });
    }
    if (els.searchClear) {
      els.searchClear.addEventListener('click', function () {
        clearSearch();
        applySearchFilter();
        if (els.searchInput) els.searchInput.focus();
      });
    }

    if (els.sortSelect) {
      els.sortSelect.addEventListener('change', function () {
        state.sort = els.sortSelect.value || 'manual';
        state.page = 1;
        loadBacks(state.model, 1, false).then(function () {
          if (state.query) runSearch();
        });
      });
    }

    function loadBacks(model, page, append) {
      var token = ++state.requestToken;
      if (!append) {
        els.cards.innerHTML = '';
        setBacksStatus(model ? 'Loading designs for ' + model + '…' : 'Pick a model above to see the designs that fit it.');
        if (els.backsCount) els.backsCount.textContent = '';
        if (els.showMoreBtn) els.showMoreBtn.hidden = true;
        if (els.showLessBtn) els.showLessBtn.hidden = true;
      }
      return fetchBacks(model, page)
        .then(function (payload) {
          if (token !== state.requestToken) return; // a newer selection superseded this
          applyBacksPayload(payload, append);
        })
        .catch(function (err) {
          if (token !== state.requestToken) return;
          console.error('[Lūneli builder] backs request failed', err);
          setBacksStatus(
            model
              ? 'Couldn’t load designs for the ' + model + '. Please refresh and try again.'
              : 'Couldn’t load designs right now.'
          );
        });
    }

    // Card interactions, delegated so injected markup needs no re-binding.
    if (els.cards) {
      els.cards.addEventListener('click', function (e) {
        var swatch = e.target.closest('.lu-swatch-dot');
        if (swatch) {
          var swCard = swatch.closest('[data-back-card]');
          if (swCard) selectCardVariant(swCard, swatch.dataset.variantId);
          return;
        }
        var addBtn = e.target.closest('[data-card-add]');
        if (addBtn) {
          var addCard = addBtn.closest('[data-back-card]');
          if (addBtn.dataset.variantId) {
            addToCartFlow(addBtn, addBtn.dataset.variantId, 1, cardPreview(addCard, activeVariant(addCard)));
          }
          return;
        }
        // Touch has no hover, so the medal toggles its label on tap. One open
        // at a time, and it must not fall through to opening the lightbox.
        var award = e.target.closest('[data-award]');
        if (award) {
          var opening = !award.classList.contains('is-open');
          closeAwards();
          if (opening) openAward(award);
          return;
        }
        var zoomBtn = e.target.closest('[data-zoom-open]');
        if (zoomBtn) {
          var zCard = zoomBtn.closest('[data-back-card]');
          if (zCard) openZoom(zCard);
        }
      });
    }

    /* The award label is position:fixed, so it can never be clipped by the
       card, the Step 02 panel or any wrapper the parent theme puts around
       the section — but that means nothing lays it out for us. These three
       functions are its layout engine. */
    var openAwardBtn = null;

    function placeAward(btn) {
      var tip = btn.querySelector('.lu-award__text');
      if (!tip) return;
      var gap = 8;
      var pad = 12; // keep this much clear of every viewport edge
      var vw = document.documentElement.clientWidth || window.innerWidth;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var medal = btn.getBoundingClientRect();

      // Measured while visibility:hidden, which still produces a box. Reset
      // the offsets first so a previous placement can't skew the size.
      tip.style.left = '0px';
      tip.style.top = '0px';
      var box = tip.getBoundingClientRect();
      var w = box.width;
      var h = box.height;

      // Sit under the medal's left edge, then pull back inside the viewport.
      var left = medal.left - 1;
      var maxLeft = vw - w - pad;
      if (left > maxLeft) left = maxLeft;
      if (left < pad) left = pad;

      var flip = 'down';
      var top = medal.bottom + gap;
      if (top + h > vh - pad) {
        var above = medal.top - h - gap;
        if (above >= pad) {
          top = above;
          flip = 'up';
        } else {
          // Neither side fits: pin it to whichever edge leaves more room.
          top = Math.max(pad, Math.min(top, vh - h - pad));
        }
      }

      tip.style.left = Math.round(left) + 'px';
      tip.style.top = Math.round(top) + 'px';
      tip.setAttribute('data-flip', flip);

      // Point the notch at the middle of the medal, unless clamping moved
      // the label so far that the notch would hang off its own corner.
      var notch = medal.left + medal.width / 2 - left;
      if (notch < 12 || notch > w - 12) {
        tip.setAttribute('data-notch', 'off');
      } else {
        tip.removeAttribute('data-notch');
        tip.style.setProperty('--lu-notch', Math.round(notch) + 'px');
      }
    }

    function openAward(btn) {
      if (!btn) return;
      openAwardBtn = btn;
      btn.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
      placeAward(btn);
    }

    function closeAwards() {
      openAwardBtn = null;
      Array.prototype.slice.call(document.querySelectorAll('[data-award].is-open')).forEach(function (b) {
        b.classList.remove('is-open');
        b.setAttribute('aria-expanded', 'false');
      });
    }

    // Hover is bound only where hover actually exists. On touch the same
    // gesture fires mouseover then click, which would open and immediately
    // close the label; leaving hover off there keeps the tap a clean toggle.
    var canHover = !!(window.matchMedia && window.matchMedia('(hover: hover)').matches);
    if (canHover) {
      document.addEventListener('mouseover', function (e) {
        var btn = e.target.closest && e.target.closest('[data-award]');
        if (btn && btn !== openAwardBtn) {
          closeAwards();
          openAward(btn);
        }
      });
      document.addEventListener('mouseout', function (e) {
        if (!openAwardBtn) return;
        var to = e.relatedTarget;
        if (to && openAwardBtn.contains(to)) return;
        try { if (openAwardBtn.matches(':focus-visible')) return; } catch (err) { /* older browsers */ }
        closeAwards();
      });
    }
    // Keyboard gets the same label, on focus rather than hover — but only for
    // real keyboard focus. A tap also focuses the medal, and if that opened
    // the label the tap's own click would read it as already open and shut it
    // again, so the badge would never open on touch. :focus-visible is the
    // line between the two.
    document.addEventListener('focusin', function (e) {
      var btn = e.target.closest && e.target.closest('[data-award]');
      if (btn) {
        var keyboard = true;
        try { keyboard = btn.matches(':focus-visible'); } catch (err) { /* older browsers */ }
        if (keyboard) openAward(btn);
      } else if (openAwardBtn) {
        closeAwards();
      }
    });
    // Tapping anywhere else, or pressing Escape, puts the label away.
    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-award]')) closeAwards();
    });
    window.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAwards();
    });
    // Fixed positioning does not follow the page, so re-place on any move.
    window.addEventListener('scroll', function () {
      if (openAwardBtn) placeAward(openAwardBtn);
    }, true);
    window.addEventListener('resize', function () {
      if (openAwardBtn) placeAward(openAwardBtn);
    });

    if (els.showMoreBtn) {
      els.showMoreBtn.addEventListener('click', function () {
        if (!state.hasNext) return;
        els.showMoreBtn.disabled = true;
        els.showMoreBtn.textContent = 'Loading…';
        loadBacks(state.model, state.page + 1, true).then(function () {
          els.showMoreBtn.disabled = false;
        });
      });
    }

    // Collapse back to the first page and return the shopper to the top of the
    // grid, so a long browse doesn't leave them scrolling back up.
    if (els.showLessBtn) {
      els.showLessBtn.addEventListener('click', function () {
        clearSearch();
        state.page = 1;
        loadBacks(state.model, 1, false).then(function () {
          if (els.cards) {
            els.cards.scrollIntoView({
              behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
              block: 'start',
            });
          }
        });
      });
    }

    // Collection pills swap which collection the grid is fetched from. The
    // model filter still applies, so "Christmas" means "Christmas backs that
    // fit the chosen phone".
    if (els.collectionPills) {
      els.collectionPills.addEventListener('click', function (e) {
        var pill = e.target.closest('[data-collection]');
        if (!pill) return;
        var handle = pill.dataset.collection;
        if (!handle || handle === state.collection) return;
        clearSearch();
        state.collection = handle;
        state.page = 1;
        Array.prototype.slice
          .call(els.collectionPills.querySelectorAll('[data-collection]'))
          .forEach(function (p) { p.classList.toggle('is-active', p === pill); });
        loadBacks(state.model, 1, false);
      });
    }

    // ---------- Zoom lightbox ----------

    // Reviews shown before "Show all N reviews" is offered.
    var REVIEW_PREVIEW = 5;

    function starMarkup(rating) {
      var filled = Math.round(Number(rating) || 0);
      var out = '';
      for (var i = 1; i <= 5; i++) out += i <= filled ? '★' : '☆';
      return out;
    }

    /**
     * The description and the reviews live on the product, not on the card.
     * Duplicating a ~3 KB description across 24 cards would more than double
     * the grid payload for something almost nobody opens, so both are fetched
     * from sections/luneli-product-detail.liquid the first time a card is
     * opened, and cached for the rest of the session.
     */
    function loadDetail(handle) {
      if (!handle) return Promise.reject(new Error('No product handle on this card'));
      if (detailCache[handle]) return Promise.resolve(detailCache[handle]);
      var url = window.LuneliCart.root() + 'products/' + handle +
        '?section_id=' + encodeURIComponent(state.detailSection);
      return fetch(url, { headers: { Accept: 'text/html' } })
        .then(function (r) {
          if (!r.ok) throw new Error('Detail request failed (' + r.status + ')');
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var payload = doc.querySelector('[data-detail-payload]');
          if (!payload) throw new Error('Detail section returned no payload');
          var descEl = payload.querySelector('[data-detail-description]');
          var revEl = payload.querySelector('[data-detail-reviews]');
          var detail = {
            desc: descEl ? descEl.innerHTML : '',
            reviews: revEl ? revEl.innerHTML : '',
            url: payload.getAttribute('data-product-url') || '',
            rating: payload.getAttribute('data-rating') || '',
            count: parseInt(payload.getAttribute('data-rating-count'), 10) || 0,
          };
          detailCache[handle] = detail;
          return detail;
        });
    }

    function setZoomTab(name) {
      if (!zoom.tabs) return;
      zoom.tabs.forEach(function (tab) {
        var on = tab.dataset.zoomTab === name;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      Object.keys(zoom.panels).forEach(function (key) {
        if (zoom.panels[key]) zoom.panels[key].hidden = key !== name;
      });
      // A hidden element measures 0, so the "Read more" decision can only be
      // made once the details panel is actually on screen.
      if (name === 'details' && zoomState && !zoomState.descExpanded && zoom.desc.innerHTML) {
        zoom.descToggle.hidden = zoom.desc.scrollHeight <= zoom.desc.clientHeight + 4;
      }
    }

    function applyDetail(detail) {
      if (!zoomState) return;
      zoomState.descExpanded = false;
      zoom.desc.innerHTML = detail.desc || '<p>No description for this design yet.</p>';
      zoom.desc.classList.add('lu-rte--clamped');
      // Only offer "Read more" when there is actually something clipped.
      zoom.descToggle.hidden = zoom.desc.scrollHeight <= zoom.desc.clientHeight + 4;
      zoom.descToggle.textContent = 'Read more';
      zoom.reviews.innerHTML = detail.reviews || '<p class="lu-review-empty">No reviews for this design yet.</p>';

      // Five to begin with, the rest revealed in place. Sending someone to the
      // product page to read reviews strands them off the builder, so the link
      // out only appears once everything here has been read AND the product
      // genuinely holds more than the metafield carries.
      var shown = zoom.reviews.querySelectorAll('.lu-review, .lu-review-fallback .review').length;
      zoomState.reviewsShown = shown;
      zoomState.detailUrl = detail.url || '';
      var capped = shown > REVIEW_PREVIEW;
      zoom.reviews.classList.toggle('lu-reviews--capped', capped);
      if (zoom.moreReviews) {
        zoom.moreReviews.hidden = !capped;
        zoom.moreReviews.textContent = 'Show all ' + shown + ' reviews';
      }
      updateAllReviewsLink(capped);
    }

    /**
     * Only offered when the product has more reviews than we were handed, and
     * only once the shopper has expanded the ones we do have. It opens in a new
     * tab so the builder, and their place in it, stays put behind.
     */
    function updateAllReviewsLink(stillCapped) {
      if (!zoom.allReviews) return;
      var total = zoomState ? zoomState.ratingCount : 0;
      var shown = zoomState ? zoomState.reviewsShown || 0 : 0;
      var url = zoomState ? zoomState.detailUrl : '';
      var worthIt = !stillCapped && url && total > shown;
      zoom.allReviews.hidden = !worthIt;
      if (worthIt) {
        zoom.allReviews.href = url + '#looxReviews';
        zoom.allReviews.textContent = 'Read all ' + total + ' reviews on the product page (opens a new tab) →';
      }
    }

    function openZoom(card) {
      if (!zoom.overlay) return;
      var variants = parseVariants(card);
      if (!variants.length) return;
      var activeId = card.dataset.activeVariant;
      var index = variants.findIndex(function (v) { return String(v.id) === String(activeId); });
      var handle = card.dataset.productHandle || '';
      var count = parseInt(card.dataset.ratingCount, 10) || 0;
      zoomState = {
        card: card,
        handle: handle,
        title: card.dataset.productTitle || '',
        rating: card.dataset.rating || '',
        ratingCount: count,
        variants: variants,
        index: index === -1 ? 0 : index,
        descExpanded: false,
        token: ++detailToken,
      };

      // The card already knows the rating, so the summary and the tab label
      // are correct before the fetch lands.
      if (zoom.rating) {
        zoom.rating.hidden = count === 0;
        if (count > 0) {
          zoom.rating.innerHTML =
            '<span class="lu-stars" aria-hidden="true">' + starMarkup(zoomState.rating) + '</span>' +
            '<span>' + zoomState.rating + ' out of 5 &middot; ' + count +
            ' review' + (count === 1 ? '' : 's') + '</span>';
        }
      }
      zoom.tabs.forEach(function (tab) {
        if (tab.dataset.zoomTab !== 'reviews') return;
        tab.textContent = count > 0 ? 'See reviews (' + count + ')' : 'See reviews';
      });

      setZoomTab('details');
      zoom.desc.innerHTML = '<p>Loading description…</p>';
      zoom.desc.classList.remove('lu-rte--clamped');
      zoom.descToggle.hidden = true;
      zoom.reviews.innerHTML = '<p class="lu-reviews-status">Loading reviews…</p>';
      zoom.reviews.classList.remove('lu-reviews--capped');
      if (zoom.allReviews) zoom.allReviews.hidden = true;
      if (zoom.moreReviews) zoom.moreReviews.hidden = true;

      // Two stacked overlays fighting each other is how the cart ended up over
      // the close button. The lightbox now sits above the cart, and opening it
      // collapses the cart sheet outright.
      setCartOpen(false);

      renderZoom();
      zoom.overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';

      var token = zoomState.token;
      loadDetail(handle)
        .then(function (detail) {
          // A different card may have been opened while this was in flight.
          if (!zoomState || zoomState.token !== token) return;
          applyDetail(detail);
        })
        .catch(function (err) {
          if (!zoomState || zoomState.token !== token) return;
          console.error('[Lūneli builder] product detail failed', err);
          zoom.desc.innerHTML = '<p>Couldn’t load the description right now.</p>';
          zoom.desc.classList.remove('lu-rte--clamped');
          zoom.descToggle.hidden = true;
          zoom.reviews.innerHTML = '<p class="lu-reviews-status">Couldn’t load reviews right now.</p>';
          if (zoom.allReviews && handle) {
            zoom.allReviews.hidden = false;
            zoom.allReviews.href = window.LuneliCart.root() + 'products/' + handle;
            zoom.allReviews.textContent = 'Open the product page (opens a new tab) →';
          }
        });
    }

    function closeZoom() {
      if (!zoom.overlay) return;
      zoom.overlay.classList.remove('is-open');
      document.body.style.overflow = '';
      zoomState = null;
    }

    function renderZoom() {
      if (!zoomState || !zoom.overlay) return;
      var v = zoomState.variants[zoomState.index];
      zoom.name.textContent = zoomState.title;
      zoom.sub.textContent = v.color ? state.model + ' · ' + v.color : state.model;
      if (v.image) zoom.image.src = v.image;
      zoom.image.alt = zoomState.title;
      zoom.price.textContent = money(v.price);
      zoom.add.disabled = !v.available;
      zoom.add.textContent = v.available ? 'Add to my collection' : 'Sold out';

      var multi = zoomState.variants.length > 1;
      zoom.nav.style.display = multi ? 'flex' : 'none';
      zoom.dots.innerHTML = '';
      if (multi) {
        zoomState.variants.forEach(function (variant, i) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.textContent = variant.color || 'Option ' + (i + 1);
          var active = i === zoomState.index;
          dot.style.cssText = 'padding:7px 13px;border-radius:999px;font:600 12px Inter;cursor:pointer;' +
            (active
              ? 'background:var(--lu-ink);color:var(--lu-bg);border:1px solid var(--lu-ink);'
              : 'background:#fff;color:var(--lu-ink);border:1px solid rgba(51,49,62,.18);');
          dot.addEventListener('click', function () {
            zoomState.index = i;
            selectCardVariant(zoomState.card, variant.id);
            renderZoom();
          });
          zoom.dots.appendChild(dot);
        });
      }

      // The description and reviews are deliberately NOT touched here.
      // renderZoom runs again on every colour change inside the lightbox, and
      // re-rendering them would wipe the fetched markup on each click.

      zoom.add.onclick = function () {
        addToCartFlow(zoom.add, v.id, 1, cardPreview(zoomState.card, v));
        closeZoom();
      };
    }

    function stepZoom(delta) {
      if (!zoomState) return;
      var n = zoomState.variants.length;
      zoomState.index = ((zoomState.index + delta) % n + n) % n;
      selectCardVariant(zoomState.card, zoomState.variants[zoomState.index].id);
      renderZoom();
    }

    if (zoom.overlay) {
      zoom.close.addEventListener('click', closeZoom);
      zoom.overlay.addEventListener('click', function (e) { if (e.target === zoom.overlay) closeZoom(); });
      zoom.modal.addEventListener('click', function (e) { e.stopPropagation(); });
      zoom.prev.addEventListener('click', function () { stepZoom(-1); });
      zoom.next.addEventListener('click', function () { stepZoom(1); });
      zoom.descToggle.addEventListener('click', function () {
        if (!zoomState) return;
        zoomState.descExpanded = !zoomState.descExpanded;
        zoom.desc.classList.toggle('lu-rte--clamped', !zoomState.descExpanded);
        zoom.descToggle.textContent = zoomState.descExpanded ? 'Show less' : 'Read more';
      });
      zoom.tabs.forEach(function (tab) {
        tab.addEventListener('click', function () { setZoomTab(tab.dataset.zoomTab); });
      });
      if (zoom.moreReviews) {
        zoom.moreReviews.addEventListener('click', function () {
          zoom.reviews.classList.remove('lu-reviews--capped');
          zoom.moreReviews.hidden = true;
          updateAllReviewsLink(false);
        });
      }
      window.addEventListener('keydown', function (e) {
        if (!zoomState) return;
        if (e.key === 'Escape') closeZoom();
        else if (e.key === 'ArrowLeft') stepZoom(-1);
        else if (e.key === 'ArrowRight') stepZoom(1);
      });
    }

    // ---------- Cart rail ----------

    /**
     * Adding fires both our own refresh and the theme's cartUpdate event, which
     * used to mean two /cart.js reads racing each other for the same answer.
     * Concurrent callers now share one request.
     */
    function refreshCart() {
      if (cartInFlight) return cartInFlight;
      cartInFlight = window.LuneliCart.getState()
        .then(renderCart)
        .catch(function (err) { console.error('[Lūneli builder] cart read failed', err); })
        .then(function () { cartInFlight = null; });
      return cartInFlight;
    }

    function renderCart(cart) {
      lastCart = cart;
      var count = cart.item_count;
      // On mobile the rail is a fixed bar over the page, so an empty one is
      // pure clutter — `is-empty` takes it off screen until there is something
      // to show. On desktop it stays put; the CSS only acts under 860px.
      cartItemCount = count;
      if (els.cartRail) {
        els.cartRail.classList.toggle('is-empty', count === 0);
        if (count === 0 && els.cartRail.classList.contains('is-open')) setCartOpen(false);
        // Collapsed, the pill's label is the only thing on screen, so it has
        // to carry the count: "View 2 items".
        if (els.cartToggleLabel && !els.cartRail.classList.contains('is-open')) {
          els.cartToggleLabel.textContent = cartToggleLabel(false);
        }
      }
      els.cartSummary.textContent = count === 0
        ? 'Nothing added yet'
        : count + ' item' + (count === 1 ? '' : 's') + ' · ' + money(cart.total_price);
      var remaining = Math.max(0, FREE_SHIP - cart.total_price);
      var pct = FREE_SHIP > 0 ? Math.min(100, Math.round((cart.total_price / FREE_SHIP) * 100)) : 100;
      els.shipFill.style.width = pct + '%';
      els.shipMsg.textContent = remaining <= 0 ? 'You’ve unlocked free shipping!' : money(remaining) + ' away from free shipping';

      els.cartLines.innerHTML = '';
      els.cartEmpty.hidden = count > 0;
      updateCartNotice(cart);
      cart.items.forEach(function (item, idx) {
        var row = document.createElement('div');
        row.style.cssText = 'display:flex;align-items:center;gap:12px';

        var thumb = document.createElement('div');
        thumb.style.cssText = 'width:40px;height:50px;border-radius:8px;flex-shrink:0;background:#fff center/cover no-repeat;border:1px solid rgba(255,250,250,.14)';
        if (item.image) thumb.style.backgroundImage = 'url("' + item.image + '")';
        row.appendChild(thumb);

        var info = document.createElement('div');
        info.style.cssText = 'flex:1;min-width:0';
        var title = document.createElement('div');
        title.style.cssText = 'font-size:13px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis';
        title.textContent = item.product_title;
        var sub = document.createElement('div');
        sub.style.cssText = 'font-size:11.5px;color:rgba(255,250,250,.55);white-space:nowrap;overflow:hidden;text-overflow:ellipsis';
        sub.textContent = item.variant_title || '';
        info.appendChild(title);
        info.appendChild(sub);
        row.appendChild(info);

        var price = document.createElement('div');
        price.style.cssText = 'font-size:13px;font-weight:700';
        price.textContent = money(item.line_price);
        row.appendChild(price);

        var removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.title = 'Remove';
        removeBtn.setAttribute('aria-label', 'Remove ' + item.product_title);
        removeBtn.textContent = '×';
        removeBtn.style.cssText = 'background:none;border:none;color:rgba(255,250,250,.5);cursor:pointer;font-size:16px;line-height:1;padding:2px';
        removeBtn.addEventListener('click', function () {
          window.LuneliCart.changeLine(idx + 1, 0).then(renderCart);
        });
        row.appendChild(removeBtn);

        els.cartLines.appendChild(row);
      });

      els.subtotal.textContent = money(cart.total_price);
    }

    var cartScrim = scope.querySelector('#luneli-cart-scrim') || document.getElementById('luneli-cart-scrim');

    function isMobileCart() { return window.matchMedia('(max-width: 860px)').matches; }

    function cartToggleLabel(open) {
      if (open) return 'Hide';
      if (cartItemCount <= 0) return 'View collection';
      return 'View ' + cartItemCount + ' item' + (cartItemCount === 1 ? '' : 's');
    }

    function setCartOpen(open) {
      if (!els.cartRail) return;
      els.cartRail.classList.toggle('is-open', open);
      if (cartScrim) cartScrim.classList.toggle('is-open', open);
      if (els.cartToggleLabel) els.cartToggleLabel.textContent = cartToggleLabel(open);
      if (els.cartChevron) els.cartChevron.textContent = open ? '▲' : '▼';
      if (els.cartToggle) els.cartToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.body.style.overflow = open ? 'hidden' : '';
    }

    if (els.cartHeader) {
      els.cartHeader.addEventListener('click', function () {
        if (isMobileCart()) setCartOpen(!els.cartRail.classList.contains('is-open'));
      });
    }
    if (cartScrim) cartScrim.addEventListener('click', function () { setCartOpen(false); });
    window.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && els.cartRail && els.cartRail.classList.contains('is-open')) setCartOpen(false);
    });
    window.addEventListener('resize', function () {
      if (!isMobileCart() && els.cartRail && els.cartRail.classList.contains('is-open')) setCartOpen(false);
    });

    if (window.FoxThemeEvents && window.PUB_SUB_EVENTS) {
      window.FoxThemeEvents.subscribe(PUB_SUB_EVENTS.cartUpdate, function () { refreshCart(); });
    }
    // Adds made outside the builder — the Adrenaline Daisy section, say — reach
    // the rail through here rather than relying on the theme's pub/sub being
    // present. refreshCart() de-duplicates, so both firing costs one request.
    document.addEventListener('luneli:cart-optimistic', function (e) {
      var detail = e.detail || {};
      optimisticAdd(detail.variantId, detail.quantity || 1, detail.preview);
    });
    document.addEventListener('luneli:cart-updated', function () { refreshCart(); });

    // ---------- Brand/model wiring ----------

    root.addEventListener('lu-picker-change', function (e) {
      var brand = e.detail.brand;
      var model = e.detail.model;
      if (brand === state.brand && model === state.model) return;
      var brandChanged = brand !== state.brand;
      state.brand = brand;
      state.model = model;
      if (brandChanged) state.shellColor = 'Black';
      clearSearch();
      state.page = 1;

      loadShellData(brand).then(renderShellStep);
      loadBacks(model, 1, false);
      updateBackorder(model);
    });

    // ---------- Back-order notice ----------
    // Which sizes are short on blanks is a theme setting, not a constant, so
    // the list is read off the section every time the builder boots.

    var boBanner = root.querySelector('[data-backorder-banner]');
    var boCartNote = root.querySelector('[data-backorder-cart]');
    var boModal = document.querySelector('[data-backorder-modal]');
    var boSeen = {};

    var boModels = String(root.dataset.backorderModels || '')
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean)
      .map(function (s) {
        // Go through the shared matcher so "iPhone 17 Air" vs "iPhone Air"
        // and stray spacing resolve the same way the variant matching does.
        return window.LuneliModels ? window.LuneliModels.canon(s) : s.toLowerCase().replace(/\s+/g, '');
      });

    function isBackordered(model) {
      if (!model || !boModels.length) return false;
      var key = window.LuneliModels ? window.LuneliModels.canon(model) : String(model).toLowerCase().replace(/\s+/g, '');
      return boModels.indexOf(key) !== -1;
    }

    // sessionStorage can throw outright in private modes and embedded views,
    // so every read and write is guarded. Losing the "already seen" memory
    // only means the popup shows again — never a broken builder.
    function boSeenKey(model) {
      return 'luneli:backorder:' + (window.LuneliModels ? window.LuneliModels.canon(model) : model);
    }
    function boWasSeen(model) {
      var k = boSeenKey(model);
      if (boSeen[k]) return true;
      try { return sessionStorage.getItem(k) === '1'; } catch (err) { return false; }
    }
    function boMarkSeen(model) {
      var k = boSeenKey(model);
      boSeen[k] = true;
      try { sessionStorage.setItem(k, '1'); } catch (err) { /* private mode */ }
    }

    // Moving focus scrolls the focused element into view, and that is what
    // would otherwise throw the shopper down to the builder when the popup
    // closes — they might have opened it from the top of the page. Every
    // focus() here passes preventScroll, and the page position is snapshotted
    // and put back immediately afterwards for the browsers that ignore it.
    var boReturnFocus = null;

    function focusWithoutScrolling(el) {
      if (!el || typeof el.focus !== 'function') return;
      var x = window.scrollX || window.pageXOffset || 0;
      var y = window.scrollY || window.pageYOffset || 0;
      try {
        el.focus({ preventScroll: true });
      } catch (err) {
        el.focus();
      }
      // Synchronous, so a browser that scrolled anyway is corrected before the
      // next paint and the shopper never sees the jump.
      if ((window.scrollX || window.pageXOffset || 0) !== x || (window.scrollY || window.pageYOffset || 0) !== y) {
        window.scrollTo(x, y);
      }
    }

    function openBackorderModal(model) {
      if (!boModal) return;
      var label = boModal.querySelector('[data-backorder-model]');
      if (label) label.textContent = model ? 'Applies to the ' + model + '.' : '';
      // Remember whatever had focus so it can be handed back on close, rather
      // than always dumping focus on the model select wherever that may sit.
      boReturnFocus = document.activeElement;
      boModal.hidden = false;
      // Next frame, so the transition has a painted "closed" state to run from.
      requestAnimationFrame(function () { boModal.classList.add('is-open'); });
      focusWithoutScrolling(boModal.querySelector('[data-backorder-close]'));
    }

    function closeBackorderModal() {
      if (!boModal || boModal.hidden) return;
      boModal.classList.remove('is-open');
      // Hand focus back where it came from, but only if that element is still
      // on the page and is not inside the dialog we are closing.
      var back = boReturnFocus;
      boReturnFocus = null;
      if (back && back !== document.body && document.contains(back) && !boModal.contains(back)) {
        focusWithoutScrolling(back);
      } else {
        var active = document.activeElement;
        if (active && boModal.contains(active) && typeof active.blur === 'function') active.blur();
      }
      window.setTimeout(function () { boModal.hidden = true; }, 200);
    }

    function updateBackorder(model) {
      var hit = isBackordered(model);
      if (boBanner) boBanner.hidden = !hit;
      if (!hit) return;
      if (boWasSeen(model)) return;
      boMarkSeen(model);
      openBackorderModal(model);
    }

    // The cart reminder answers a different question from the banner: not
    // "what size is selected" but "what is in the collection you are about to
    // buy". A shopper can add an affected size and then move the picker
    // elsewhere; the delay follows the items, not the picker.
    function cartHasBackordered(cart) {
      if (!boModels.length || !cart || !cart.items || !cart.items.length) return false;
      return cart.items.some(function (item) {
        // Variant titles read "iPhone 18 Pro" or "iPhone 18 Pro / Black Cow
        // Print" — the phone model is always the first option.
        var vt = String(item.variant_title || '');
        return isBackordered(vt.split(' / ')[0].trim());
      });
    }

    function updateCartNotice(cart) {
      if (!boCartNote) return;
      boCartNote.hidden = !cartHasBackordered(cart);
    }

    if (boModal) {
      boModal.addEventListener('click', function (e) {
        // Backdrop click closes; a click inside the panel must not.
        if (e.target.closest('[data-backorder-close]') || !e.target.closest('[data-backorder-panel]')) {
          closeBackorderModal();
        }
      });
      window.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeBackorderModal();
      });
    }

    // A model may already be chosen from a previous visit — the store restores
    // it, and the picker can finish upgrading before this listener is attached,
    // so its event is not something we can rely on having seen. Read the shared
    // store directly rather than trusting state.model, which is still '' here
    // when that race goes the wrong way.
    var boInitial = state.model;
    if (!boInitial && window.LuneliStore) {
      var saved = window.LuneliStore.get();
      if (saved && saved.model) boInitial = saved.model;
    }
    updateBackorder(boInitial);

    // First load: no model selected yet, but this teaches us the real filter
    // parameter name from the section itself.
    loadBacks('', 1, false);
    refreshCart();
  }

  function bootAll(container) {
    var scope = container || document;
    Array.prototype.slice
      .call(scope.querySelectorAll ? scope.querySelectorAll('#luneli-builder') : [])
      .forEach(initBuilder);
  }

  ready(function () { bootAll(document); });

  document.addEventListener('shopify:section:load', function (e) { bootAll(e.target); });
})();
